import type { WorkspaceSearchHit } from "@rakazo/contracts";
import { WORKSPACE_APPS, workspaceResourcePaths } from "@rakazo/contracts";
import {
  Button,
  Command,
  CommandDialog,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@rakazo/ui-web";
import { ArrowUpRight, Search } from "lucide-react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createSpaceRpc } from "../lib/rpc";
import { errorText } from "../lib/user-error";
import {
  WorkspaceSearchHotkeyContext,
  WorkspaceWindowFocusContext,
} from "../lib/workspace-local-state";

export function WorkspaceSearch({
  spaceId,
  open: controlledOpen,
  onOpenChange,
}: {
  spaceId: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const focused = useContext(WorkspaceWindowFocusContext);
  const handlesShortcut = useContext(WorkspaceSearchHotkeyContext);
  const client = useMemo(() => createSpaceRpc(spaceId), [spaceId]);
  const navigate = useNavigate();
  const [localOpen, setLocalOpen] = useState(false);
  const open = controlledOpen ?? localOpen;
  const setOpen = onOpenChange ?? setLocalOpen;
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<WorkspaceSearchHit[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (controlledOpen !== undefined || !focused || !handlesShortcut) return;
    const key = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setLocalOpen((value) => !value);
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [controlledOpen, focused, handlesShortcut]);
  useEffect(() => {
    let live = true;
    setHits([]);
    setError("");
    setBusy(Boolean(open && q.trim()));
    const timer = setTimeout(() => {
      if (!open || !q.trim()) return;
      void client.workspace
        .search({ q: q.trim() })
        .then((result) => {
          if (live) setHits(result);
        })
        .catch((e) => {
          if (live) setError(errorText(e));
        })
        .finally(() => {
          if (live) setBusy(false);
        });
    }, 200);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [client, q, open]);
  const select = (hit: WorkspaceSearchHit) => {
    setOpen(false);
    if (hit.url && /^https:\/\//.test(hit.url)) {
      window.open(hit.url, "_blank", "noopener,noreferrer");
      return;
    }
    const destination = workspaceResourcePaths(hit);
    if (destination) navigate(destination.webPath);
  };
  const shown = q.trim()
    ? hits
    : WORKSPACE_APPS.map((app) => ({
        id: app.id,
        kind: "app" as const,
        title: app.name,
        subtitle: app.description,
      }));
  return (
    <>
      {controlledOpen === undefined && (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Search workspace"
          onClick={() => setOpen(true)}
        >
          <Search size={17} />
        </Button>
      )}
      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title="Search workspace"
        description="Find agents, files, activity and apps"
        className="sm:max-w-xl"
      >
        <Command shouldFilter={false}>
          <CommandInput
            value={q}
            onValueChange={setQ}
            placeholder="Search workspace…"
            maxLength={200}
          />
          <CommandList className="max-h-[55vh]">
            {busy && (
              <p className="p-4 text-xs text-muted-foreground" role="status">
                Searching…
              </p>
            )}
            {error && (
              <p className="p-4 text-xs text-destructive" role="alert">
                {error}
              </p>
            )}
            {!busy && !error && <CommandEmpty>No results</CommandEmpty>}
            {shown.map((hit) => (
              <CommandItem
                key={`${hit.kind}:${hit.id}`}
                value={`${hit.kind}:${hit.id}`}
                onSelect={() => select(hit)}
                className="gap-3 rounded-lg px-3 py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{hit.title}</p>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">{hit.subtitle}</p>
                </div>
                <span className="text-[10px] capitalize text-muted-foreground">{hit.kind}</span>
                <ArrowUpRight size={13} />
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  );
}
